<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as Sentry from '@sentry/vue'
import UIKit from '@pocketprep/ui-kit'
import { liveTriviaModule } from '@/store/liveTrivia/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { subscriptionModule } from '@/store/subscription/module'
import { upgradeModule } from '@/store/upgrade/module'
import { userModule } from '@/store/user/module'
import { formatCountdown, formatScheduledAt } from '@/components/LiveTrivia/countdown'
import { useServerNow } from '@/components/LiveTrivia/useServerNow'

const {
    Icon,
    Tag,
} = UIKit

/** Inside this window before start, the card switches to a live countdown. */
const startingSoonWindowMs = 15 * 60 * 1000
const upcomingGamePollMs = 60 * 1000

const router = useRouter()
const serverNowMs = useServerNow()
let upcomingGamePollTimer: ReturnType<typeof setInterval> | null = null

const isDarkMode = computed(() => userModule.state.settings.isDarkMode)

const currentExam = computed(() => examMetadataModule.getters.getCurrentExamMetadata())

const upcomingGame = computed(() => liveTriviaModule.getters.getUpcomingGameForExam(currentExam.value?.examGuid))

const isPremium = computed(() => {
    // The server's answer is authoritative for the game's exam; fall back to local subscriptions before it loads.
    return upcomingGame.value?.me?.isPremium
        ?? !!subscriptionModule.getters.getSubscriptionForExamId(currentExam.value?.objectId)
})

const msUntilStart = computed(() => {
    if (!upcomingGame.value) {
        return 0
    }
    return Date.parse(upcomingGame.value.scheduledAt) - serverNowMs.value
})

const cardPhase = computed(() => {
    if (upcomingGame.value && upcomingGame.value.status !== 'scheduled') {
        return 'live'
    }
    return msUntilStart.value <= startingSoonWindowMs ? 'startingSoon' : 'upcoming'
})

const isPlayerInGame = computed(() => {
    const player = upcomingGame.value?.me
    return !!player?.joined && player.alive
})

const statusLabel = computed(() => {
    if (cardPhase.value === 'live') {
        return isPlayerInGame.value ? 'Live now · You\'re still in!' : 'Live now · Watch along'
    }
    if (cardPhase.value === 'startingSoon') {
        return msUntilStart.value > 0
            ? `Starting in ${formatCountdown(msUntilStart.value)}`
            : 'Starting any moment'
    }
    const scheduledAtMs = upcomingGame.value ? Date.parse(upcomingGame.value.scheduledAt) : 0
    return `${formatScheduledAt(scheduledAtMs, serverNowMs.value)} · in ${formatCountdown(msUntilStart.value)}`
})

const actionLabel = computed(() => {
    if (cardPhase.value === 'live') {
        return isPlayerInGame.value ? 'Rejoin' : 'Watch'
    }
    if (!isPremium.value) {
        return 'Upgrade'
    }
    return upcomingGame.value?.me?.joined ? 'Open' : 'Join'
})

const fetchUpcomingGame = async () => {
    const examGuid = currentExam.value?.examGuid
    if (!examGuid || !liveTriviaModule.getters.getIsLiveTriviaExam(examGuid)) {
        return
    }
    try {
        await liveTriviaModule.actions.fetchUpcomingGame(examGuid)
    } catch (error) {
        // The card is optional: if the lookup fails it simply stays hidden.
        Sentry.captureException(error)
    }
}

const handleClick = () => {
    const game = upcomingGame.value
    if (!game) {
        return
    }
    // Watching a game in progress is open to everyone; joining one needs Premium.
    if (cardPhase.value !== 'live' && !isPremium.value) {
        upgradeModule.actions.openUpgradeModal()
        return
    }
    router.push({ name: 'live-trivia', params: { gameId: game.id } })
}

// When the countdown runs out, check whether the host has opened the game.
watch(() => msUntilStart.value <= 0, (hasStartTimePassed) => {
    if (hasStartTimePassed && cardPhase.value !== 'live') {
        fetchUpcomingGame()
    }
})

watch(() => currentExam.value?.examGuid, () => {
    fetchUpcomingGame()
})

onMounted(() => {
    fetchUpcomingGame()
    upcomingGamePollTimer = setInterval(() => {
        fetchUpcomingGame()
    }, upcomingGamePollMs)
})

onBeforeUnmount(() => {
    if (upcomingGamePollTimer) {
        clearInterval(upcomingGamePollTimer)
    }
})
</script>

<template>
    <div
        v-if="upcomingGame"
        class="live-trivia-card"
        :class="{
            'live-trivia-card--dark': isDarkMode,
            'live-trivia-card--live': cardPhase === 'live',
        }"
        tabindex="0"
        role="button"
        :aria-label="`Live Trivia. ${statusLabel}. ${actionLabel}${isPremium ? '' : ', Premium'}`"
        @click="handleClick"
        @keydown.enter="handleClick"
        @keydown.space.prevent="handleClick"
        @mousedown.prevent
    >
        <div
            class="live-trivia-card__icon"
            :class="{ 'live-trivia-card__icon--dark': isDarkMode }"
        >
            <Icon type="lightning" />
        </div>
        <div class="live-trivia-card__text">
            <div class="live-trivia-card__title-row">
                <span class="live-trivia-card__title">Live Trivia</span>
                <span
                    v-if="cardPhase === 'live'"
                    class="live-trivia-card__live-pill"
                >
                    <span class="live-trivia-card__live-dot" aria-hidden="true" />
                    Live
                </span>
                <Tag
                    v-if="!isPremium"
                    class="live-trivia-card__premium"
                    size="small"
                    :is-dark-mode="isDarkMode"
                >
                    <template #tagMessage>
                        Premium
                    </template>
                </Tag>
            </div>
            <div
                class="live-trivia-card__status"
                :class="{
                    'live-trivia-card__status--dark': isDarkMode,
                    'live-trivia-card__status--soon': cardPhase === 'startingSoon',
                }"
            >
                {{ statusLabel }}
            </div>
        </div>
        <span
            class="live-trivia-card__action"
            :class="{ 'live-trivia-card__action--dark': isDarkMode }"
            aria-hidden="true"
        >
            {{ actionLabel }}
        </span>
    </div>
</template>

<style lang="scss" scoped>
.live-trivia-card {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
    min-height: 72px;
    padding: 14px 16px;
    border-radius: 6px;
    border: 1px solid rgba($pewter, 0.85);
    box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
    background: $white;
    color: $brand-black;
    user-select: none;
    outline: none;
    cursor: pointer;

    &:hover {
        border-color: $slate;
    }

    &:focus-visible {
        border-color: $brand-blue;
        box-shadow: 0 0 0 1px $brand-blue;
    }

    &--live {
        border-color: $brand-red;
    }

    &--dark {
        background: $brand-black;
        border-color: $slate;
        box-shadow: 0 1px 4px 0 rgba($jet, 0.3);
        color: $white;

        &:hover {
            border-color: rgba($white, 0.6);
        }

        &:focus-visible {
            border-color: $banana-bread;
            box-shadow: 0 0 0 1px $banana-bread;
        }
    }

    &--live#{&}--dark {
        border-color: $rosa;
    }

    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        margin-right: 12px;
        border-radius: 18px;
        background: $barely-purple;
        color: $cosmos;

        &--dark {
            background: rgba($orchid, 0.15);
            color: $orchid;
        }
    }

    &__text {
        flex: 1;
        min-width: 0;
    }

    &__title-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
    }

    &__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
    }

    &__live-pill {
        display: inline-flex;
        align-items: center;
        padding: 2px 6px;
        border-radius: 4px;
        background: $brand-red;
        color: $white;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.6px;
        line-height: 14px;
        text-transform: uppercase;
    }

    &__live-dot {
        width: 6px;
        height: 6px;
        margin-right: 4px;
        border-radius: 3px;
        background: $white;
        animation: live-trivia-card-pulse 1.4s ease-in-out infinite;
    }

    &__status {
        margin-top: 2px;
        font-size: 14px;
        line-height: 18px;
        color: $slate;

        &--dark {
            color: $fog;
        }

        &--soon {
            color: $brand-blue;
            font-weight: 600;
            font-variant-numeric: tabular-nums;
        }

        &--soon#{&}--dark {
            color: $banana-bread;
        }
    }

    &__action {
        flex-shrink: 0;
        margin-left: 12px;
        font-size: 14px;
        font-weight: 600;
        color: $brand-blue;

        &--dark {
            color: $banana-bread;
        }
    }
}

@keyframes live-trivia-card-pulse {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.3;
    }
}

@media (prefers-reduced-motion: reduce) {
    .live-trivia-card__live-dot {
        animation: none;
    }
}
</style>
